import { sum } from '@shared/lib/money';
import type { ExareGdsServiceRefundInfo, FlightFragment } from '@websky/graphql';
import { ALL_SEGMENTS_KEY, getFlightSegmentIds } from '@shared/lib/flight';
import type { Money } from '../../../../Money/types';
import type { ServiceType } from '../../../../CardExtra/types';
import { getServiceTypeByGdsService } from '../../../../CardExtra/utils';
import { groupServicesByType } from '../../../../RefundSummary/utils';

import { getRefundableGdsServices } from './getRefundableGdsServices';
import type { GdsServicesRefundInfo } from './getRefundableGdsServices';

export interface RefundServiceProductItem {
	key: string;
	passengerId: string;
	productIds: number[];
	segmentKey: string;
	name: string;
	price?: Money;
}

export interface RefundServiceCardViewModel {
	type: ServiceType;
	items: RefundServiceProductItem[];
	productIds: number[];
}

interface RefundServiceProductGroup {
	passengerId: string;
	name: string;
	productsBySegment: Map<string, RefundServiceProduct[]>;
}

interface RefundServiceProduct {
	productId: number;
	price: Money;
}

const getRefundProductGroupKey = (serviceId: string, passengerId: string, emdNumber?: string | null): string =>
	[serviceId, passengerId, emdNumber ?? ''].join('/');

const buildRefundServiceGroups = (
	refundServices: ExareGdsServiceRefundInfo[],
	flightSegmentIds: string[]
): RefundServiceProductGroup[] => {
	const groups = new Map<string, RefundServiceProductGroup>();

	const productCounts = new Map<string, number>();
	const selectedProductSegmentIdsMap = new Map<string, Set<string>>();

	refundServices.forEach(refundService => {
		for (const seat of refundService.services?.seats ?? []) {
			const productId = seat.product?.id;
			const segmentId = seat.segment?.id;

			if (!productId || !segmentId) {
				continue;
			}

			const segmentIdSet = selectedProductSegmentIdsMap.get(productId) ?? new Set();
			segmentIdSet.add(segmentId);

			selectedProductSegmentIdsMap.set(productId, segmentIdSet);
		}

		for (const service of refundService.services?.gdsServices?.services ?? []) {
			if (!service.products?.length || !service.segmentIds?.length) {
				continue;
			}

			for (const product of service.products) {
				const productId = product.id;

				const segmentIdSet = selectedProductSegmentIdsMap.get(productId) ?? new Set();
				service.segmentIds.filter(Boolean).forEach(id => segmentIdSet.add(id));

				selectedProductSegmentIdsMap.set(productId, segmentIdSet);
			}
		}

		refundService.gdsServiceProductsRefundInfo.forEach(productInfo => {
			const passengerId = productInfo.product.passengerId;
			const key = getRefundProductGroupKey(refundService.service.id, passengerId, productInfo.product.emdNumber);

			productCounts.set(key, (productCounts.get(key) ?? 0) + 1);
		});
	});

	refundServices.forEach(refundService => {
		const serviceInfo = refundService.service;
		const serviceRfiscOrId = serviceInfo.rfisc ?? serviceInfo.id;

		refundService.gdsServiceProductsRefundInfo.forEach(productInfo => {
			const productId = parseInt(productInfo.product.id, 10);
			const passengerId = productInfo.product.passengerId;

			const productSegmentIds = [...(selectedProductSegmentIdsMap.get(productInfo.product.id) ?? [])];
			const isAllSegments = productSegmentIds.length === flightSegmentIds.length;
			const segmentKeys = isAllSegments ? [ALL_SEGMENTS_KEY] : productSegmentIds;

			const productCountKey = getRefundProductGroupKey(
				serviceInfo.id,
				passengerId,
				productInfo.product.emdNumber
			);
			const count = productCounts.get(productCountKey) ?? 1;

			segmentKeys.forEach(segmentKey => {
				const keyValues = [serviceRfiscOrId, passengerId];

				if (serviceInfo.type === 'Seat') {
					keyValues.push(segmentKey);
				} else {
					keyValues.push(count.toString());
				}

				const groupKey = keyValues.join('/');

				const group = groups.get(groupKey) ?? {
					passengerId,
					name: serviceInfo.name,
					productsBySegment: new Map<string, RefundServiceProduct[]>()
				};

				const products = group.productsBySegment.get(segmentKey) ?? [];
				products.push({ productId, price: productInfo.sumToRefund });

				group.productsBySegment.set(segmentKey, products);
				groups.set(groupKey, group);
			});
		});
	});

	return [...groups.values()];
};

const getRefundServiceItemsFromGroups = (
	groups: RefundServiceProductGroup[],
	flightSegmentIds: string[]
): RefundServiceProductItem[] => {
	return groups.reduce<RefundServiceProductItem[]>((items, group) => {
		const segmentKeys = [...group.productsBySegment.keys()];
		const isAllSegments = segmentKeys.length === flightSegmentIds.length;

		(isAllSegments ? [ALL_SEGMENTS_KEY] : segmentKeys).forEach(segmentKey => {
			const products =
				segmentKey === ALL_SEGMENTS_KEY
					? [...group.productsBySegment.values()].reduce(
							(products, segmentProducts) => [...products, ...segmentProducts],
							[]
					  )
					: group.productsBySegment.get(segmentKey) ?? [];

			if (products.length) {
				const productIds = products.map(product => product.productId);

				items.push({
					key: productIds.join('|'),
					passengerId: group.passengerId,
					productIds,
					segmentKey,
					name: group.name,
					price: products.every(product => product.price)
						? sum(...products.map(product => product.price))
						: undefined
				});
			}
		});

		return items;
	}, []);
};

const getRefundServiceItems = (
	refundServices: ExareGdsServiceRefundInfo[],
	flight: FlightFragment | undefined
): RefundServiceProductItem[] => {
	const flightSegmentIds = getFlightSegmentIds(flight);
	const groups = buildRefundServiceGroups(refundServices, flightSegmentIds);

	return getRefundServiceItemsFromGroups(groups, flightSegmentIds);
};

export const getRefundServicesCardViewModels = (
	services: GdsServicesRefundInfo = [],
	flight?: FlightFragment,
	onlyRefundableProducts = true
): RefundServiceCardViewModel[] => {
	const refundableServices = onlyRefundableProducts ? getRefundableGdsServices(services) : services;
	const servicesByType = groupServicesByType(refundableServices);
	const cards: RefundServiceCardViewModel[] = [];

	servicesByType.forEach(servicesByCurrentType => {
		const firstService = servicesByCurrentType[0].service;
		const items = getRefundServiceItems(servicesByCurrentType as ExareGdsServiceRefundInfo[], flight);

		cards.push({
			type: getServiceTypeByGdsService(firstService),
			items,
			productIds: items.reduce((acc, item) => [...acc, ...item.productIds], [])
		});
	});

	return cards;
};
